/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

// These add the necessary browser polyfills needed based on browserlist
// property in package.json.
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import $ from 'jquery';
// Special handling for libraries that like the `window`
/* eslint-disable-next-line no-global-assign */
window.jQuery = $;
import _ from 'lodash';
import Handlebars from 'handlebars';
import Swag from 'swag';
import Backbone from 'backbone';
import Marionette from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Backgrid from 'backgrid';
import NProgress from 'nprogress';
import psaJQuery from 'psa-core/lib/jquery';
import psaHandlebars from 'psa-core/lib/handlebars';

import Config from '@config/Config';
import importAll from '@/util/importAll';
import handleAjaxError from '@/util/handleAjaxError';
import jqueryExtensions from '@/util/jqueryExtensions';

import 'select2/dist/js/select2.full';
import 'messenger';
import 'bootstrap';
import 'bootstrap-datepicker';
import 'psa-backgrid/CustomHeaderCell';

import '@/util/sortingExtensions';

import '../node_modules/select2/dist/css/select2.css';
import '../node_modules/bootstrap-datepicker/dist/css/bootstrap-datepicker.css';
import '../node_modules/select2-bootstrap-theme/dist/select2-bootstrap.css';
import '@/styles/bootstrap.scss';
import '../node_modules/bootstrap-icons/font/bootstrap-icons.css';
import '../node_modules/messenger/build/css/messenger.css';
import '../node_modules/messenger/build/css/messenger-theme-flat.css';
import '../node_modules/backgrid/lib/backgrid.css';
import '../node_modules/font-awesome/css/font-awesome.css';
import '../node_modules/ladda/dist/ladda-themeless.min.css';
import '../node_modules/nprogress/nprogress.css';

import '@/styles/icon-addon.css';
import '@/styles/datepicker.css';
import '@/styles/global.css';
import '@/styles/fonts.css';
import '@/styles/parsley.scss';
import '@/styles/cursor.scss';
import '@/styles/buttons.css';

$('html').attr('lang', 'en');
// Parsley Configuration
window.ParsleyConfig = {
  successClass: '',
  errorClass: 'has-error',
  classHandler(el) {
    return el.$element.closest('.form-group');
  },
  errorsWrapper: '<span class="help-block"></span>',
  errorTemplate: '<span></span>',
  excluded:
    'input[type=button], input[type=submit], input[type=reset], input[type=hidden], [disabled], [disabled] *, input.select2-search__field'
};
require('parsleyjs');
importAll(require.context('./', true, /\/parsley\/..*\.js$/));

// Messenger Configuration
Messenger.options = {
  extraClasses: 'messenger-fixed messenger-on-bottom messenger-on-left',
  theme: 'flat'
};

// Bootstrap Datepicker Configuration
$.fn.datepicker.defaults.autoclose = true;
$.fn.datepicker.defaults.format = 'mm/dd/yyyy';
$.fn.datepicker.defaults.assumeNearbyYear = true;

// Backbone Syphon Configuration
Syphon.ignoredTypes.push('[disabled]');
Syphon.ignoredTypes.push('[disabled] *');
Syphon.ignoredTypes.push('file');
Syphon.InputWriters.register('radio', ($el, value) => {
  const checked = _.isNil(value) ? false : $el.val() === value.toString();
  $el.prop('checked', checked);
});
Syphon.InputWriters.register('select', ($el, value) => {
  if (value) {
    $el.val(value);
  } else {
    const $emptyOption = $el.find('option[value=""]');
    if ($emptyOption.length) {
      $el.val('');
    }
  }
});

// NProgress Configuration
NProgress.configure({ minimum: 0.4 });

// Separate InputReaderSet for elastic search views
Syphon.SearchInputReaderSet = new Syphon.InputReaderSet();
Syphon.SearchInputReaderSet.registerDefault(($el) => {
  const input = $el.val();
  if (_.isString(input)) {
    return input.trim();
  }
  return input;
});
Syphon.SearchInputReaderSet.register('checkbox', Syphon.InputReaders.get('checkbox'));

// jQuery Extensions
$.serverConfig = Config;
// _.extend($, psaJQuery.extensions);
_.extend($, jqueryExtensions);
$.ajaxSetup(psaJQuery.ajaxSetup);
// $(document).ajaxError(handleAjaxError);

// Handlebars Extensions
psaHandlebars.registerHelpers(Handlebars, psaHandlebars.helpers);
Swag.registerHelpers(Handlebars);
importAll(require.context('./', true, /\/handlebars\/..*\.js$/));

// Handlebars Partials Registration
const partials = importAll(require.context('../', true, /\/partials\/..*\.hbs$/));
for (const partial of Object.keys(partials)) {
  const partialName = partial.match(/\w+(?=\.hbs)/);
  Handlebars.registerPartial(partialName, partials[partial]);
}

// Backbone Extensions
Backbone.$ = $;
Object.assign(Backbone.View.prototype, Marionette.Events);

// Backgrid Extensions
Object.assign(Backgrid.Grid.prototype, {
  className: 'backgrid table table-striped'
});

if (window.__agent) {
  window.__agent.start(Backbone, Marionette);
}

/**
 * Many environments (such as Node.js) report unhandled promise rejections to
 * the console by default. You can prevent that from happening by adding a
 * handler for unhandledrejection events that—in addition to any other tasks you
 * wish to perform—calls preventDefault() to cancel the event, preventing it
 * from bubbling up to be handled by the runtime's logging code. This works
 * because unhandledrejection is cancelable.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event#Preventing_default_handling
 */
window.addEventListener('unhandledrejection', ({ reason }) => {
  // eslint-disable-next-line no-console
  throw new Error(reason);
  // console.error('Promise Rejected:', reason);
});

import '@/ContextLoader';
